import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AddressCardBlock } from '@core/services/ai/ai-assistant.service';
import { buildMailtoUrl } from '@shared/utils/mailto.util';

@Component({
  selector: 'app-ai-address-card-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (block.title) {
      <div class="ac__title">{{ block.title }}</div>
    }
    <div class="ac">
      <div class="ac__head">
        <mat-icon class="ac__icon">place</mat-icon>
        <div class="ac__main">
          @if (block.name) {
            <div class="ac__name">{{ block.name }}</div>
          }
          <div class="ac__address">{{ block.address }}</div>
        </div>
        <button
          type="button"
          mat-icon-button
          class="ac__action"
          [matTooltip]="'Open in Google Maps'"
          (click)="openInMaps()">
          <mat-icon>open_in_new</mat-icon>
        </button>
        <button
          type="button"
          mat-icon-button
          class="ac__action"
          [matTooltip]="copied ? 'Copied!' : 'Copy address'"
          (click)="copyAddress()">
          <mat-icon>{{ copied ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
      @if (block.fields?.length) {
        <dl class="ac__fields">
          @for (f of block.fields; track f.label) {
            <div class="ac__field">
              <dt>{{ f.label }}</dt>
              <dd>{{ f.value }}</dd>
            </div>
          }
        </dl>
      }
      @if (block.contacts?.length) {
        <div class="ac__contacts">
          @for (c of block.contacts; track c.value) {
            <a
              class="ac__contact"
              [href]="hrefFor(c)"
              [attr.target]="c.kind === 'url' ? '_blank' : null"
              [attr.rel]="c.kind === 'url' ? 'noopener noreferrer' : null">
              <mat-icon>{{ iconFor(c.kind) }}</mat-icon>
              <span class="ac__contact-label">{{ c.label }}</span>
              <span class="ac__contact-value">{{ c.value }}</span>
            </a>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .ac__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .ac {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        overflow: hidden;
      }
      .ac__head {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 14px;
      }
      .ac__icon {
        font-size: 22px;
        width: 22px;
        height: 22px;
        color: #6366f1;
        flex-shrink: 0;
        margin-top: 2px;
      }
      .ac__main {
        flex: 1;
        min-width: 0;
      }
      .ac__name {
        font-weight: 700;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.95);
        overflow-wrap: anywhere;
      }
      .ac__address {
        font-size: 13px;
        color: rgba(15, 23, 42, 0.7);
        line-height: 1.4;
        overflow-wrap: anywhere;
        margin-top: 2px;
      }
      .ac__action {
        flex-shrink: 0;
      }
      .ac__fields {
        margin: 0;
        padding: 8px 14px 12px;
        border-top: 1px solid rgba(15, 23, 42, 0.06);
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 6px 16px;
      }
      .ac__field {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .ac__field dt {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.5);
      }
      .ac__field dd {
        margin: 0;
        font-size: 13px;
        color: rgba(15, 23, 42, 0.85);
        overflow-wrap: anywhere;
      }
      .ac__contacts {
        display: flex;
        flex-direction: column;
        border-top: 1px solid rgba(15, 23, 42, 0.06);
      }
      .ac__contact {
        display: grid;
        grid-template-columns: 24px auto 1fr;
        gap: 8px;
        align-items: center;
        padding: 8px 14px;
        text-decoration: none;
        color: inherit;
        font-size: 13px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.04);
      }
      .ac__contact:last-child {
        border-bottom: none;
      }
      .ac__contact:hover {
        background: rgba(99, 102, 241, 0.05);
      }
      .ac__contact mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: #6366f1;
      }
      .ac__contact-label {
        color: rgba(15, 23, 42, 0.5);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .ac__contact-value {
        font-weight: 600;
        color: rgba(15, 23, 42, 0.85);
        text-align: right;
        overflow-wrap: anywhere;
      }
    `
  ]
})
export class AiAddressCardBlockComponent {
  @Input({ required: true }) block!: AddressCardBlock;
  copied = false;

  openInMaps(): void {
    const q = encodeURIComponent(this.block.address);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${q}`,
      '_blank',
      'noopener,noreferrer'
    );
  }

  async copyAddress(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.block.address);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    } catch {
      // best-effort
    }
  }

  /**
   * The href for one contact row.
   *
   * The email branch went through raw interpolation until #372. The value here
   * comes from a card the ASSISTANT produced out of an extracted document, so
   * it is uncontrolled text that happens to be labelled "email" — exactly the
   * case where `?bcc=` inside the value would have become a second mailto
   * parameter. `buildMailtoUrl` encodes it (and keeps `@` literal, RFC 6068).
   *
   * It returns `''` for an empty address, and `href=""` resolves to the current
   * page — a click would reload the SPA. The bare `mailto:` is inert instead,
   * which is what the old code did for that degenerate row.
   */
  hrefFor(c: { kind: 'phone' | 'email' | 'url'; value: string }): string {
    if (c.kind === 'phone') return `tel:${c.value.replace(/\s+/g, '')}`;
    if (c.kind === 'email') return buildMailtoUrl(c.value) || 'mailto:';
    return c.value.startsWith('http') ? c.value : `https://${c.value}`;
  }

  iconFor(kind: 'phone' | 'email' | 'url'): string {
    if (kind === 'phone') return 'phone';
    if (kind === 'email') return 'mail';
    return 'link';
  }
}
